import React, { useEffect, useState, createRef, FocusEvent } from "react"; // 
import { Badge, Button } from "react-bootstrap";
import { MailErrorMessages, Attachment, FileSizeLimits } from "src/common";
import { Icon } from "src/components";
import { filesize } from "filesize";
import _ from "lodash";

type AllProps = {
  opened: boolean;
  attachments: Attachment[];
  handleAttachmentChange: (attachments: Attachment[]) => void;
  handleAttachmentErrors: (errors: { empty_files: string[]; oversized_files: string[] }) => void;
  resetAttachmentOpened: () => void;
}

function ComposeMailAttachmentComponent(props: AllProps) {
  const { opened, attachments, handleAttachmentChange, handleAttachmentErrors, resetAttachmentOpened } = props;
  const [badge_attachments, setBadgeAttachments] = useState<Attachment[]>();
  const attachment_ref = createRef<HTMLInputElement>();

  useEffect(() => {
    opened && handleOpenAttachmentModal();
  }, [opened]);

  useEffect(() => {
    const _att = _.filter(attachments, (_attachment: Attachment) => !_attachment.is_inline);
    setBadgeAttachments(_att);
  }, [attachments]);

  // FILE UPLOAD:
  // --------------------------------------------------------------------------------------
  // Description: Handle Attachment dialog and attachment processing for mail
  function handleOpenAttachmentModal() {
    !!attachment_ref.current ?
      attachment_ref.current.click() : console.error(MailErrorMessages.error_opening_editor_file_upload);
    resetAttachmentOpened();
  }

  // Description: File Upload to Compose and update parent statement
  function handleAddAttachments(e: FocusEvent<HTMLInputElement>) {
    const files = e.target.files;
    const empty_files: string[] = [];
    const oversized_files: string[] = [];
    if (!!files && files.length > 0) {
      const _attachments = _.compact(_.map(files, (file: File) => {
        if (!file.size) {
          // NOTE: if file size is 0B then block the attachment and pass a warning message.
          empty_files.push(file.name);
        } else if (file.size > FileSizeLimits.MAIL_UPLOAD_FILE_MAX_SIZE) {
          // NOTE: block files over the max attachment size to avoid loading huge files into memory.
          oversized_files.push(file.name);
        } else {
          return Attachment.fromFile(file);
        };
      }));
      const new_attachments = attachments.length > 0 ? _attachments.concat(attachments.slice()) : _attachments;
      (new_attachments.length > 0) && handleAttachmentChange(new_attachments);
      (empty_files.length > 0 || oversized_files.length > 0) && handleAttachmentErrors({ empty_files, oversized_files });
      // NOTE: Reset the value of the input otherwise the user cannot try again
      if (attachment_ref.current) {
        attachment_ref.current.value = "";
      }
    }
  }

  // Description: Remove Attachments and update parent statement
  function handleRemoveAttachment(_attachment: Attachment): void {
    const new_attachments = attachments.filter(att => att.uuid !== _attachment.uuid);
    handleAttachmentChange(new_attachments);
  }

  // Description: Render each badge
  // NOTE: Dont show inline attachments as badges
  function AttachmentBadge(props: { attachment: Attachment }) {
    const _attachment: Attachment = props.attachment;
    return <Badge pill bg="light">
      <Icon className="ficon-paperclip" />
      {_attachment.filename} <span>{filesize(_attachment.size).toString()}</span>
      <Button variant="icon" size="sm" aria-label={`Click to remove ${_attachment.filename}`} onClick={() => handleRemoveAttachment(_attachment)}>
        <Icon className="ficon-x" />
      </Button>
    </Badge>;
  }

  return <>
    {
      (!!badge_attachments && badge_attachments.length > 0) &&
      <div className="compose-row attachments">
        {
          _.map(badge_attachments, (attachment: Attachment, i: number) => {
            return <AttachmentBadge key={`attachment_${i}`} attachment={attachment} />;
          })
        }
      </div>
    }
    <input ref={attachment_ref} className="d-none" multiple type="file" onChange={handleAddAttachments} />
  </>;
}

export default React.memo(ComposeMailAttachmentComponent);
